import { Injectable, computed, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Event,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  Router,
} from '@angular/router';
import { filter } from 'rxjs';
import { AppHeaderStore } from '../state/stores/app-header.store';
import { AppMenuStore } from '../state/stores/app-menu.store';
import { AppPopoverStore } from '../state/stores/app-popover.store';
import { AppSearchbarStore } from '../state/stores/app-searchbar.store';
import { AppSideDrawersStore } from '../state/stores/app-side-drawers.store';
import { FeatureImportComponentsStore } from '../state/stores/feature-import-components.store';

@Injectable({
  providedIn: 'root',
})
export class ApplicationService {
  private readonly _router = inject(Router);

  private readonly _popoverStore = inject(AppPopoverStore);
  public readonly featureImportComponents = inject(FeatureImportComponentsStore);
  public readonly searchbar = inject(AppSearchbarStore);
  public readonly header = inject(AppHeaderStore);
  public readonly menu = inject(AppMenuStore);
  public readonly sideDrawers = inject(AppSideDrawersStore);

  public readonly breakpointWidth = this.menu.breakpointWidth;
  public readonly breakpointHeight = this.menu.breakpointHeight;
  public readonly breakpointOrientation = this.menu.breakpointOrientation;

  public readonly isPopoversActive = computed(
    () => this.header.isDrawerOpen() || (!this.menu.isInline() && this.menu.isOpen()) || this.sideDrawers.isOpen(),
  );

  public closePopovers() {
    this.header.closeDrawer();
    this.sideDrawers.close();
    this.closeOverlayMenu();
  }

  public constructor() {
    effect(this.closeOtherDrawers.bind(this));
    let isInitialNavigation = true;
    this._router.events.pipe(filter(isNavigationFinished), takeUntilDestroyed()).subscribe((e) => {
      // A keep-open request only applies to the navigation it was made for.
      const keepMenuOpen = this.menu.consumeKeepOpenOnNavigation();
      if (!(e instanceof NavigationEnd || e instanceof NavigationError)) return;
      if (isInitialNavigation) {
        isInitialNavigation = false;
        return;
      }
      this.header.closeDrawer();
      this.sideDrawers.close();
      if (!keepMenuOpen) {
        this.closeOverlayMenu();
      }
    });
  }

  private closeOverlayMenu() {
    if (!this.menu.isInline()) {
      this.menu.close();
    }
  }

  private closeOtherDrawers() {
    const activeId = this._popoverStore.activeId();
    if (activeId === 'menu') {
      this.header.closeDrawer();
      this.sideDrawers.close();
    } else if (activeId === 'header') {
      this.menu.close();
      this.sideDrawers.close();
    } else if (activeId === 'side') {
      this.menu.close();
      this.header.closeDrawer();
    }
    this._popoverStore.setActive(null);
  }
}

function isNavigationFinished(e: Event): boolean {
  return (
    e instanceof NavigationEnd ||
    e instanceof NavigationError ||
    e instanceof NavigationSkipped ||
    // A redirect cancels the navigation and starts a new one, which the request still applies to.
    (e instanceof NavigationCancel && e.code !== NavigationCancellationCode.Redirect)
  );
}
